// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import QtyField, { QtyFieldStyles } from "@awc/App/Components/Qty/QtyField";
import { xMask } from "@awc/XDL/Utilities";
import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    label?: string;
    // XNG-Changes
    extendedStyles?: QtyFieldStyles;
    labelOnLeft?: boolean;
    formId?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
            css: css`
                font-size: 10px;
                text-transform: uppercase;
                font-weight: 600;
            `,
        },
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    // XNG-Change
    labelOnLeft = true,
    formId,
}) => {
    const localOnQtyOrderedChange = (value: string, onError?: () => void) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            onError?.();
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    // const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));

    const xChangeHandler = (event: any) => {
        event.currentTarget.value = Number(xMask.qty(event.currentTarget.value));
        changeHandler(event);
    };

    // const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));

    // XNG-Changes: use QtyField
    if (editable && !isOutOfStock(cartLine) && !cartLine.properties["lrb"]) {
        return (
            <QtyField
                label={label ?? translate("QTY")}
                onChange={xChangeHandler}
                onKeyDown={keyDownHandler}
                onBlur={blurHandler}
                value={value}
                disabled={!cart.canModifyOrder || cartLine.isPromotionItem || cart.type === "Job" || isLoading[cart.id]}
                extendedStyles={extendedStyles}
                data-test-selector="cartline_qty"
                onClick={event => event.currentTarget.focus()}
                labelOnLeft={labelOnLeft}
                showLabel={true}
            />
        );
    }

    // XNG-Changes
    return (
        <QtyField
            label={label ?? translate("QTY")}
            extendedStyles={extendedStyles}
            value={cartLine.qtyOrdered!}
            data-test-selector="quoteDetail_cardLine_qty"
            editable={false}
            formId={formId}
        />
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
